/* eslint-disable max-lines-per-function */
import * as turf from '@turf/turf';
import { CheckCircle, Eye, EyeOff, Move, Target03, Truck01 } from '@untitled-ui/icons-react/build/cjs';
import mapboxgl from 'mapbox-gl';
import moment from 'moment-timezone';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPhysicallyActiveLeg } from 'src/app/Orders/OrderStatus/nba.helpers';
import { getLatestStartedLegLocation, getStartedLegLocationUpdates } from 'src/app/Track/track.helpers';
import {
    clearTrackOrderDetail,
    // selectSelectedLeg,
    selectActiveScheduleSection,
    setActiveScheduleSection,
    setFullRouteDistance,
    setRemainingDistance,
    setSelectedLeg,
    setTrackDetailLoading,
    setTrackOrderDetail,
    trackDetailLoading,
    trackOrderDetail as trackOrderDetailSelector,
} from 'src/app/Track/track.slice';
import BrokenIcon from 'src/assets/img/icons/link-broken.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import FilterDropdown from 'src/components/FilterDropdown';
import LoadingCover from 'src/components/LoadingCover';
import Header from 'src/components/PageSectionLayout/Header/Header';
import ToastBodyContainer from 'src/components/ToastContainer';
import TooltipCmp from 'src/components/TooltipCmp';
import { getBadgeLabelByOrderStatus, getOrderStatusBadgeType, ORDER_STATUS } from 'src/constants/common';
import { ROUTES, SHARE_LINK_PREVIEW_PARAM } from 'src/constants/routes';
import { BadgeType } from 'src/interface/common';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { appearance as appearanceSelector, setAppearance, setHandlingUnitOptions } from 'src/redux/basic.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { ERROR_STATUS, getSharedRadarOrder, LOAD_STATE, LoadState, setShareToken } from 'src/services/ShareLinkService';
import { getPortalOrigin } from 'src/utils/portalOrigin';
import { DrivingRouteResult, getDrivingRouteWithWaypoints, getRemainingRouteMiles } from 'src/utils/RouteUtils';
import 'mapbox-gl/dist/mapbox-gl.css';

import SharedPageTopBar from '../SharedQuote/SharedPageTopBar';
import OrderMetaCards, { LegSwitcher } from '../Track/RadarOrderDetail/OrderMetaCards';
import RadarMapAboveComponentNew from '../Track/RadarOrderDetail/RadarMapAboveComponentNew';
import CarrierDetailsSection from '../Track/RadarOrderDetail/sections/CarrierDetailsSection';
import DeliveredStateSection from '../Track/RadarOrderDetail/sections/DeliveredStateSection';
import InTransitStateSection from '../Track/RadarOrderDetail/sections/InTransitStateSection';
import LocationUpdatesSection from '../Track/RadarOrderDetail/sections/LocationUpdatesSection';
import OrderDetailsSection from '../Track/RadarOrderDetail/sections/OrderDetailsSection';
import PickupStateSection from '../Track/RadarOrderDetail/sections/PickupStateSection';
import { makeFormatScheduleDisplay } from '../Track/RadarOrderDetail/track.helpers';

import SharedDocumentsSection from './sections/SharedDocumentsSection';

// Mobile status card date format — plain and suffix-free, e.g. "Thu, Oct 8, 2026". No weekday token:
// the shared schedule helper prepends the weekday itself (full name), so shortWeekday trims it.
const HEADER_DATE_FORMAT = 'MMM D, YYYY';

// First-paint cover (see coverPhase). The settle beat lets the just-loaded cards and route
// paint under the cover; the fade is the same length the public tracking page uses for its own, so
// a search runs one continuous dissolve. The cap is the escape hatch when the map never reports in.
const FIRST_PAINT_SETTLE_MS = 120;
const FIRST_PAINT_FADE_MS = 300;
const FIRST_PAINT_MAX_MS = 6000;
const shortWeekday = (value: string) => value.replace(/\b(Mon|Tues|Wednes|Thurs|Fri|Satur|Sun)day\b/g, (day) => day.slice(0, 3));

// ── Error state ────────────────────────────────────────────────────────────

const ErrorState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div className="flex flex-col items-center justify-center text-center gap-5 max-w-[1480px] mx-auto rounded-xl w-full bg-white mt-4 max-lg:h-full max-lg:mb-4 lg:min-h-[500px] max-lg:w-[calc(100%-2rem)] max-lg:mx-4">
        <div className="flex flex-col items-center justify-center">
            <img src={BrokenIcon} alt="broken link" className="w-[80px] h-[80px] object-contain" />
            <div className="flex flex-col items-center gap-1.5 mt-5">
                <h1 className="text-[20px] font-semibold leading-[30px] text-grayLight900">{title}</h1>
                <p className="text-[14px] font-normal leading-[20px] text-gray500 max-lg:px-2">{body}</p>
            </div>
            <ButtonCmp className="btn-outline-primary mt-4" onClick={() => (window.location.href = `${getPortalOrigin()}${ROUTES.LOGIN}`)}>
                Login to Portal
            </ButtonCmp>
        </div>
    </div>
);

// ── Main component ─────────────────────────────────────────────────────────

// Private request counter for this page. Deliberately NOT the Track module's shared counter:
// that one is bumped by RadarOrderDetail's mount/unmount, and an in-app navigation from
// /track/:id to a share link would invalidate this page's in-flight route draw, leaving the
// map blank with no retry.
let shareRouteRequestSeq = 0;

const bumpShareRouteRequestSeq = (): number => {
    shareRouteRequestSeq += 1;

    return shareRouteRequestSeq;
};
const getShareRouteRequestSeq = (): number => shareRouteRequestSeq;

interface IShareRadarOrderProps {
    /**
     * Set by the public tracking page (track.westernalliancelogistics.com): after a WAL ID search it
     * renders THIS page so the visitor sees exactly what the tracking link shows. The share token
     * comes from the lookup instead of ?t=, and the WAL ID search sits in the middle of the top bar.
     * Absent, the page is unchanged.
     *
     * `loadingLabel` is what the tracking page was already showing while it looked the WAL ID up.
     * Carrying it across means the first-paint cover below says the same thing the cover it replaces
     * said, so one wait does not read as two loading screens (client, 2026-10-07).
     */
    publicTracking?: { token: string; searchContent: ReactNode; loadingLabel?: string };
}

const ShareRadarOrder = ({ publicTracking }: IShareRadarOrderProps = {}) => {
    mapboxgl.accessToken = window?.MAPBOX_ACCESSTOKEN!;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = publicTracking?.token ?? searchParams.get('t');
    // Preview opt-out flag set by the Share Tracking Link modal — see SHARE_LINK_PREVIEW_PARAM.
    const isPreviewMode = searchParams.get(SHARE_LINK_PREVIEW_PARAM) === '1';
    const loggedInUser = useAppSelector(currentUserSelector);

    const appearanceData = useAppSelector(appearanceSelector);
    // Read the latest appearance inside the fetch callback without making it an effect dependency
    // (which would re-run the fetch every time appearance changes).
    const appearanceRef = useRef(appearanceData);
    appearanceRef.current = appearanceData;
    const trackOrderDetail = useAppSelector(trackOrderDetailSelector);
    const isDetailLoading = useAppSelector(trackDetailLoading);
    // const selectedLeg = useAppSelector(selectSelectedLeg);

    // ── Stage switcher ──────────────────────────────────────────────────────
    // Which schedule card shows is driven by the switcher's selection, NOT by orderStatus —
    // orderStatus only seeds the DEFAULT. Mirrors CP Track + wal-frontend-v2 Radar. The
    // 'delivery' fallback also covers post-delivery statuses (completed / invoice-*), which
    // previously rendered no schedule card at all. A share link carries no router state, so
    // there is no nav-state first-paint hint here — the loading branch shows Pickup instead.
    const activeScheduleSection = useAppSelector(selectActiveScheduleSection);

    const resolvedScheduleSection: 'pickup' | 'inTransit' | 'delivery' = useMemo(() => {
        if (activeScheduleSection) return activeScheduleSection;
        // Not picked up yet (booked or dispatched): the pickup schedule. The public tracking page
        // opens booked shipments here too (Lucas, 2026-10-01).
        if (trackOrderDetail?.orderStatus === ORDER_STATUS.BOOKED || trackOrderDetail?.orderStatus === ORDER_STATUS.DISPATCHED) return 'pickup';
        if (trackOrderDetail?.orderStatus === ORDER_STATUS.IN_TRANSIT) return 'inTransit';

        return 'delivery';
    }, [activeScheduleSection, trackOrderDetail?.orderStatus]);

    // Last location-ping time (falling back to actual pickup), in transit only. Feeds the desktop
    // header's "Last Update" and the mobile map's "Updated" badge.
    const lastPingAt = useMemo(() => {
        if (isDetailLoading || trackOrderDetail?.orderStatus !== ORDER_STATUS.IN_TRANSIT) return null;

        const activeLeg = getPhysicallyActiveLeg(trackOrderDetail?.legs) ?? trackOrderDetail?.legs?.[0] ?? null;

        return getLatestStartedLegLocation(trackOrderDetail?.legs ?? [])?.updatedAt ?? activeLeg?.schedule?.actualPickupDatetime ?? null;
    }, [trackOrderDetail?.orderStatus, trackOrderDetail?.legs, isDetailLoading]);

    // Plain "Sep 30, 2026 · 7:42 AM" instead of the ordinal-suffix form.
    const headerLastUpdate = useMemo(
        () =>
            lastPingAt
                ? moment
                      .utc(lastPingAt)
                      .tz(appearanceData?.timezone || 'UTC')
                      .format('MMM D, YYYY · h:mm A')
                : '',
        [lastPingAt, appearanceData?.timezone]
    );

    // Bare status badge — colour comes from getOrderStatusBadgeType exactly as everywhere else.
    const plainStatusBadge = useMemo(() => {
        const orderStatus = trackOrderDetail?.orderStatus;

        if (isDetailLoading || !orderStatus) return null;

        return (
            <BadgeCmp style="modern" mainClassName="bg-white rounded-md" type={getOrderStatusBadgeType(orderStatus) as BadgeType}>
                {getBadgeLabelByOrderStatus(orderStatus)}
            </BadgeCmp>
        );
    }, [trackOrderDetail?.orderStatus, isDetailLoading]);

    // Always-visible ETA beside the status badge. The Delivery Schedule card also carries this,
    // but it sits behind the stage switcher (default card is Pickup / In-Transit), so a
    // recipient opening the link would otherwise have to click to find the ETA. Once delivered,
    // the actual delivery date replaces the estimate. Reads the LAST leg — the final drop is the
    // shipment's ETA regardless of which leg the map pager has selected.
    const etaInfo = useMemo(() => {
        if (isDetailLoading || !trackOrderDetail?.orderStatus) return null;

        const legs = trackOrderDetail?.legs ?? [];
        const lastLeg = legs[legs.length - 1] ?? null;
        const schedule = lastLeg?.schedule ?? null;
        const consigneeTimezone = lastLeg?.consignee?.timezone || null;

        if (!schedule) return null;

        const actualDeliveryDatetime = schedule.actualDeliveryDatetime || null;
        const isDelivered = trackOrderDetail.orderStatus !== ORDER_STATUS.DISPATCHED && trackOrderDetail.orderStatus !== ORDER_STATUS.IN_TRANSIT && Boolean(actualDeliveryDatetime);

        if (isDelivered) {
            return {
                isDelivered: true,
                label: t('Radar.Delivered') as string,
                valueHtml: moment
                    .utc(actualDeliveryDatetime)
                    .tz(consigneeTimezone || appearanceData?.timezone || 'UTC')
                    .format('MMM D, YYYY'),
                headerValueHtml: moment
                    .utc(actualDeliveryDatetime)
                    .tz(consigneeTimezone || appearanceData?.timezone || 'UTC')
                    .format(`ddd, ${HEADER_DATE_FORMAT}`),
            };
        }

        const { display } = makeFormatScheduleDisplay('delivery', schedule, consigneeTimezone);

        if (!display?.formattedDateRange || display.formattedDateRange === '-') return null;

        // The desktop header and the mobile status card use a plain, suffix-free format ("Thu, Oct 8, 2026");
        // the mobile route card keeps the ordinal-suffix form the Delivery Schedule card uses.
        const { display: headerDisplay } = makeFormatScheduleDisplay('delivery', schedule, consigneeTimezone, HEADER_DATE_FORMAT);

        return {
            isDelivered: false,
            label: t('Radar.Est_Delivery_Date') as string,
            headerLabel: t('Radar.Est_Delivery') as string,
            valueHtml: display.formattedDateRange as string,
            headerValueHtml: shortWeekday((headerDisplay?.formattedDateRange as string) || (display.formattedDateRange as string)),
        };
    }, [trackOrderDetail?.orderStatus, trackOrderDetail?.legs, isDetailLoading, appearanceData?.timezone, t]);

    // Desktop header meta: Last Update only, as a plain label/value pair behind a hairline divider
    // (final reference design). Unboxed, so the status badge is the only chip on the line. Last
    // Update only exists while in transit (lastPingAt). Est. Delivery used to sit beside it and was
    // dropped from the header on request — etaInfo still feeds the mobile status card and the
    // Delivery Schedule card, so the memo above stays.
    const headerMeta = useMemo(() => {
        if (!headerLastUpdate) return null;

        return (
            <div className="flex items-center ml-1 border-l border-borderPrimary divide-x divide-borderPrimary whitespace-nowrap">
                <div className="flex items-center gap-1.5 px-3">
                    <span className="text-xs font-medium text-grayLight600">{t('Radar.Last_Update_Label')}</span>
                    <span className="text-xs font-semibold text-grayLight900">{headerLastUpdate}</span>
                </div>
            </div>
        );
    }, [headerLastUpdate, t]);

    const scheduleOptions = useMemo(
        () => [
            { value: 'pickup', label: t('Radar.Pickup_Schedule') as string },
            { value: 'inTransit', label: t('Radar.In_Transit_Schedule') as string },
            { value: 'delivery', label: t('Radar.Delivery_Schedule') as string },
        ],
        [t]
    );

    const handleScheduleSelect = useCallback(
        (value: string) => {
            dispatch(setActiveScheduleSection(value as 'pickup' | 'inTransit' | 'delivery'));
        },
        [dispatch]
    );

    const scheduleSwitcher = (
        <FilterDropdown
            options={scheduleOptions}
            selectedValue={resolvedScheduleSection}
            onSelect={handleScheduleSelect}
            hideOnSingleOption={false}
            labelClassName="text-sm font-medium text-grayLight900"
            dropdownPosition="left"
            isDefaultLabelColor={false}
            isLoading={isDetailLoading}
        />
    );

    // Mobile sheet (Schedule tab) — same switcher, Figma card-title type (12/18 medium).
    const mobileScheduleSwitcher = (
        <FilterDropdown
            options={scheduleOptions}
            selectedValue={resolvedScheduleSection}
            onSelect={handleScheduleSelect}
            hideOnSingleOption={false}
            labelClassName="text-xs leading-[18px] font-medium text-grayLight900"
            // Figma card title: 8px title → chevron, chevron in text-secondary (#344054)
            triggerClassName="!gap-2 [&>svg]:!text-textSecondary"
            // Compact menu to match the sheet's 12/18 rows: 4px list padding, 30px items.
            parentClassNameOfDropdown="!p-1 flex flex-col gap-0.5"
            listItemClassName="!px-2 !py-1.5 !leading-[18px]"
            // Menu width 180px (FilterDropdown hardcodes min-w-[200px] on the menu panel; keep its default container classes).
            containerClassName="relative inline-block [&>div.absolute]:!min-w-[180px]"
            dropdownPosition="left"
            isDefaultLabelColor={false}
            isLoading={isDetailLoading}
        />
    );

    // ── Mobile sheet tabs (Figma "Track Link - V3 (Tabs layout)") ───────────
    // Updates follows the existing Location Updates gate: hidden pre-pickup (booked/dispatched).
    const isUpdatesTabVisible = trackOrderDetail?.orderStatus !== ORDER_STATUS.BOOKED && trackOrderDetail?.orderStatus !== ORDER_STATUS.DISPATCHED;
    const mobileTabs = useMemo(
        () => [
            { value: 'overview' as const, label: t('ShareRadarOrder.Tab_Overview') as string },
            { value: 'schedule' as const, label: t('ShareRadarOrder.Tab_Schedule') as string },
            { value: 'order' as const, label: t('ShareRadarOrder.Tab_Order') as string },
            ...(isUpdatesTabVisible ? [{ value: 'updates' as const, label: t('ShareRadarOrder.Tab_Updates') as string }] : []),
        ],
        [t, isUpdatesTabVisible]
    );

    // Status card at the top of the sheet — carries the status, last update and ETA / delivered date
    // the old peek row showed. Delivered statuses get the success check, everything else the truck.
    const isDeliveredStatus = !!trackOrderDetail?.orderStatus && ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(trackOrderDetail.orderStatus);
    // null = no tab picked yet → default by status (in transit opens on Updates, everything else on
    // Overview). Resolved at render, so it follows the status once the order data arrives.
    const [activeMobileTab, setActiveMobileTab] = useState<'overview' | 'schedule' | 'order' | 'updates' | null>(null);
    const defaultMobileTab = !isDetailLoading && trackOrderDetail?.orderStatus === ORDER_STATUS.IN_TRANSIT ? 'updates' : 'overview';
    const pickedMobileTab = activeMobileTab ?? defaultMobileTab;
    const resolvedMobileTab = pickedMobileTab === 'updates' && !isUpdatesTabVisible ? 'overview' : pickedMobileTab;
    const statusCardTitle = isDeliveredStatus ? 'Delivery Completed!' : trackOrderDetail?.orderStatus ? getBadgeLabelByOrderStatus(trackOrderDetail.orderStatus) : '';
    // Trusted helper HTML only (from moment / makeFormatScheduleDisplay).
    const statusCardSubtitleHtml = [
        isDeliveredStatus && trackOrderDetail?.orderId ? `Route #${trackOrderDetail.orderId}` : '',
        // Only the ETA here (HEADER_DATE_FORMAT) — the "Updated" time sits in the map's order-ID badge.
        etaInfo ? `${etaInfo.headerLabel ?? etaInfo.label} ${etaInfo.headerValueHtml}` : '',
    ]
        .filter(Boolean)
        .join(' • ');
    // Same delivered date / ETA line, shown under the consignee in the Overview route timeline.
    const consigneeSubText = etaInfo ? `${etaInfo.label} ${etaInfo.valueHtml}` : '';

    const [state, setState] = useState<LoadState>(LOAD_STATE.LOADING);
    const [errorMessage, setErrorMessage] = useState('');
    const [isShowDeliveredCard, setIsShowDeliveredCard] = useState(true);
    const [isVisible, setIsVisible] = useState(() => window.innerWidth >= 1024);
    const [sheetSnap, setSheetSnap] = useState<'collapsed' | 'default' | 'expanded'>('default');
    const [sheetDragOffset, setSheetDragOffset] = useState(0);
    const touchStartYRef = useRef(0);
    // Tracks the current sheet snap position so the map padding stays correct
    // inside memoised callbacks that would otherwise close over a stale value.
    const sheetSnapRef = useRef<'collapsed' | 'default' | 'expanded'>('default');

    // Redirect logged-in users to the authenticated track detail page.
    // Separate effect so it fires correctly even when auth rehydrates after
    // order data has loaded (avoids stale closure on the fetch effect).
    // Uses masterOrderId (DB integer) which is what /track/:id expects.
    //
    // Skipped in preview mode: the Share Tracking Link modal's Preview button opens this page
    // with ?preview=1 so the (always signed-in) internal user can see the recipient's view.
    // Redirecting them to their own detail page made Preview impossible to use.
    useEffect(() => {
        // The public search must never bounce a visitor into the private app.
        if (isPreviewMode || publicTracking) return;

        if (loggedInUser && (trackOrderDetail as any)?.masterOrderId) {
            navigate(`/track/${(trackOrderDetail as any).masterOrderId}`, { replace: true });
        }
    }, [isPreviewMode, publicTracking, loggedInUser, trackOrderDetail, navigate]);

    // A cancelled shipment has nothing to track. Show the in-page notice — this used to
    // navigate to the (private) orders list, which bounced anonymous recipients to /login.
    useEffect(() => {
        if (trackOrderDetail?.orderStatus === ORDER_STATUS.CANCELLED) {
            setState(LOAD_STATE.CANCELLED);
        }
    }, [trackOrderDetail?.orderStatus]);

    // ── Map refs (identical to RadarOrderDetailNew) ─────────────────────────
    const sheetScrollRef = useRef<HTMLDivElement | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const mapReadyRef = useRef<boolean>(false);
    // State mirror of mapReadyRef — refs don't trigger effects, so changes to
    // mapReadyRef alone can't re-fire the route-drawing effect. This state
    // variable flips to true once on map 'load', giving the effect a reactive
    // dependency so it re-runs and draws the route even when data arrived first.
    const [mapReady, setMapReady] = useState(false);
    // First paint. The page renders its whole layout as soon as it mounts, so until the detail
    // arrives a visitor watched empty cards fill in and then the map tiles arrive underneath them —
    // on the public tracking page that is what a WAL ID search opened onto. A cover sits over the
    // frame until the detail has loaded AND Mapbox has painted once, then fades off: up -> fading ->
    // gone. The map container stays mounted under it the whole time, which is what lets the map
    // initialise and paint behind the cover instead of popping in after it.
    const [coverPhase, setCoverPhase] = useState<'up' | 'fading' | 'gone'>('up');
    const routeBoundsRef = useRef<mapboxgl.LngLatBounds | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const animationTimeoutRef = useRef<number | null>(null);
    const currentMarkerPositionRef = useRef<[number, number] | null>(null);
    const completedMarkerStyleRef = useRef<string>('');
    const markersRef = useRef<{ completed: mapboxgl.Marker[]; current: mapboxgl.Marker | null }>({
        completed: [],
        current: null,
    });

    // ── Fetch shared data ───────────────────────────────────────────────────

    useEffect(() => {
        if (!token) {
            setState(LOAD_STATE.INVALID_TOKEN);

            return;
        }

        setShareToken(token);
        dispatch(setTrackDetailLoading(true));
        dispatch(setTrackOrderDetail(null));

        getSharedRadarOrder()
            .then((response: any) => {
                const data = response?.data;

                if (!data) {
                    setState(LOAD_STATE.NOT_FOUND);

                    return;
                }

                dispatch(setTrackOrderDetail(data));

                // Seed org identity into appearance so useOrgBranding resolves the same org name
                // the authenticated app gets from getUserDetails — without it the name falls back
                // to a different value and the org stops being recognised as the carrier, so the
                // org logo is replaced by initials.
                //
                // Merged onto the existing appearance, never replacing it: setAppearance overwrites
                // wholesale, so a bare payload would drop timezone and the rest of the row.
                //
                // Anonymous viewers only. Gated on the auth token rather than on redux state:
                // `auth` is rehydrated by redux-persist while `basic` (appearance) is not, so
                // loggedInUser is racy on a fresh load, and AuthCheck wraps this route too —
                // getUserDetails runs in parallel and owns appearance for a signed-in viewer.
                // Seeding there would leave them holding this payload's subset of the row.
                if (data?.orgBranding && !localStorage.getItem('token')) {
                    dispatch(
                        setAppearance({
                            ...(appearanceRef.current ?? {}),
                            web_title: data.orgBranding.name,
                            logo: data.orgBranding.logo,
                            logoUrl: data.orgBranding.logoUrl,
                            mcNumber: data.orgBranding.mcNumber,
                        })
                    );
                }

                // Seed handlingUnitOptions from the API response so OrderDetailsSection
                // can resolve labels without the authenticated app-init flow.
                const dims: any[] = data?.dimensions ?? [];
                const seen = new Set<number>();
                const unitOptions = dims.reduce((acc: any[], dim: any) => {
                    if (dim?.handlingUnit && !seen.has(dim.handlingUnit) && dim?.handlingUnitName) {
                        seen.add(dim.handlingUnit);
                        acc.push({ value: dim.handlingUnit, label: dim.handlingUnitName });
                    }

                    return acc;
                }, []);

                if (unitOptions.length > 0) {
                    dispatch(setHandlingUnitOptions(unitOptions));
                }

                const legs = data?.legs ?? [];

                if (legs.length > 0) {
                    // Default the leg pager to the physically active leg — same rule as the
                    // authenticated detail page (useRadarData) and the order page's NBA card.
                    const activeLeg = getPhysicallyActiveLeg(legs);
                    const activeIndex = Math.max(
                        0,
                        legs.findIndex((leg: any) => leg?.legDbId === activeLeg?.legDbId)
                    );
                    dispatch(setSelectedLeg({ leg: legs[activeIndex], index: activeIndex }));
                }

                setState(LOAD_STATE.SUCCESS);
            })
            .catch((error: any) => {
                const status = error?.response?.status;

                if (status === ERROR_STATUS.EXPIRED) setState(LOAD_STATE.EXPIRED);
                else if (status === ERROR_STATUS.NOT_FOUND) setState(LOAD_STATE.NOT_FOUND);
                else {
                    setErrorMessage(error?.response?.data?.message || error?.message || '');
                    setState(LOAD_STATE.ERROR);
                }
            })
            .finally(() => {
                dispatch(setTrackDetailLoading(false));
            });

        return () => {
            setShareToken(null);
            dispatch(clearTrackOrderDetail());
        };
    }, [token, dispatch]);

    // ── Full-route distance (mirrors useRadarData hook effect) ──────────────

    useEffect(() => {
        const legs = trackOrderDetail?.legs ?? [];
        if (legs.length === 0) return;

        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];
        const shipperLng = Number(firstLeg?.shipper?.longitude);
        const shipperLat = Number(firstLeg?.shipper?.latitude);
        const consigneeLng = Number(lastLeg?.consignee?.longitude);
        const consigneeLat = Number(lastLeg?.consignee?.latitude);
        const mapToken = (window as any)?.MAPBOX_ACCESSTOKEN;

        if (!shipperLng || !shipperLat || !consigneeLng || !consigneeLat || !mapToken) return;

        const orderStatus = trackOrderDetail?.orderStatus;
        const hasLocationUpdates = orderStatus !== ORDER_STATUS.DISPATCHED && orderStatus !== ORDER_STATUS.BOOKED;
        // Newest-first list — the shared fetcher reverses internally so the route runs
        // start → oldest ping → … → newest ping → end (same as WAL radar).
        // STARTED legs only — pings on booked/dispatched legs must not shape the route.
        const allLocationUpdates = hasLocationUpdates ? getStartedLegLocationUpdates(legs) : [];
        const shipperCountry = firstLeg?.shipper?.country;
        const consigneeCountry = lastLeg?.consignee?.country;
        const isSameCountry = Boolean(shipperCountry && consigneeCountry && shipperCountry === consigneeCountry);
        const hasOutOfCountry = allLocationUpdates.some((u: any) => u?.country && u.country !== shipperCountry);
        const preventCrossBorder = isSameCountry && !hasOutOfCountry;

        // Shared segmented fetcher (25-coord chunking + max-distance/NoRoute pairwise
        // splits) — a raw single Directions request 422s on long multi-waypoint routes.
        getDrivingRouteWithWaypoints(
            { lat: shipperLat, lng: shipperLng },
            allLocationUpdates.map((u: any) => ({ lat: Number(u.latitude), lng: Number(u.longitude) })),
            { lat: consigneeLat, lng: consigneeLng },
            preventCrossBorder,
            mapToken
        )
            .then(({ distanceMiles }) => {
                if (distanceMiles) {
                    dispatch(setFullRouteDistance(distanceMiles));
                }
            })
            .catch(() => dispatch(setFullRouteDistance(0)));
    }, [trackOrderDetail, dispatch]);

    // ── Map padding ─────────────────────────────────────────────────────────

    const getMapFitPadding = () => {
        const isMobile = window.innerWidth < 1024;

        // Desktop padding matches wal-frontend-v2's Radar detail so the route frames
        // identically: top clears the floating header + marker label, left clears the sidebar.
        if (!isMobile) return { top: 100, right: 100, bottom: 60, left: 590 };

        // On mobile the bottom sheet overlays the map, so push the route up
        // by however many pixels the sheet currently occupies at the bottom.
        // Add 60 px of extra breathing room so end-points don't sit at the edge.
        const snap = sheetSnapRef.current;
        const vh = window.innerHeight;
        // 8 % of screen width for horizontal breathing room (≈ 30 px on 375 px device)
        const hPad = Math.round(window.innerWidth * 0.08);

        let bottomPad = 100; // collapsed: just the drag-handle bar (~72 px) + buffer
        if (snap === 'default') bottomPad = Math.round(vh * 0.4) + 60;
        else if (snap === 'expanded') bottomPad = Math.round(vh - 60) + 30;

        return { top: 100, right: hPad, bottom: bottomPad, left: hPad };
    };

    const fitMapToRoute = useCallback(() => {
        if (!mapRef.current || !routeBoundsRef.current) return;
        mapRef.current.fitBounds(routeBoundsRef.current, { padding: getMapFitPadding(), maxZoom: 8 });
    }, []);

    // ── Marker factories (identical to RadarOrderDetailNew) ─────────────────

    const createStartMarker = useCallback(() => {
        const el = document.createElement('div');
        el.innerHTML = `
            <svg width="32" height="37" viewBox="0 0 32 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g filter="url(#filter_portal_start)">
                    <path d="M13.1131 7.85152C18.4392 6.39047 23.9413 9.5237 25.4023 14.8498C26.8633 20.1759 23.7301 25.6779 18.404 27.139C13.0779 28.6 7.57589 25.4668 6.11484 20.1407C4.65379 14.8146 7.78702 9.31257 13.1131 7.85152Z" fill="#6172F3"/>
                    <path d="M24.4379 15.1143C25.7529 19.9078 22.933 24.8597 18.1395 26.1746C13.346 27.4895 8.39416 24.6696 7.07921 19.8762C5.76427 15.0827 8.58418 10.1308 13.3777 8.81589C18.1711 7.50095 23.123 10.3209 24.4379 15.1143Z" stroke="white" stroke-width="2"/>
                </g>
                <defs>
                    <filter id="filter_portal_start" x="-1.88742" y="-1.9368" width="35.2924" height="40.6496" filterUnits="userSpaceOnUse">
                        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                        <feOffset dy="3.57143"/>
                        <feGaussianBlur stdDeviation="1.78571"/>
                        <feComposite in2="hardAlpha" operator="out"/>
                        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1"/>
                        <feBlend mode="normal" in="SourceGraphic" in2="effect1" result="shape"/>
                    </filter>
                </defs>
            </svg>`;

        return new mapboxgl.Marker(el);
    }, []);

    const createStartMarkerActive = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 14px',
            'height: 14px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #9B8AFB',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    const createEndMarkerActive = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 14px',
            'height: 14px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #7A5AF8',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    // Junction dot — 16px so intermediate stops read clearly (matches RadarOrderDetail).
    const createWaypointMarker = useCallback(() => {
        const el = document.createElement('div');
        el.style.cssText = [
            'width: 16px',
            'height: 16px',
            'border-radius: 50%',
            'border: 2px solid #FFF',
            'background: #6172F3',
            'box-shadow: 0 3.571px 3.571px 0 rgba(0,0,0,0.10), 0 3.571px 7.143px -1.786px rgba(16,24,40,0.10)',
        ].join('; ');

        return new mapboxgl.Marker(el);
    }, []);

    const createCurrentMarker = useCallback(() => {
        const el = document.createElement('div');
        el.className = 'parcel-arrow z-[1]';
        el.innerHTML = `
            <div class="w-[12px] -mt-px">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none" class="w-full" id="marker-arrow-svg">
                    <path d="M12.6657 2.67916C12.2439 2.4686 11.7476 2.4686 11.3258 2.67916C10.9525 2.86552 10.7595 3.19367 10.6676 3.36083C10.5675 3.543 10.4649 3.77692 10.362 4.01162L3.05073 20.6813C2.93566 20.9436 2.82421 21.1976 2.7529 21.4084C2.69129 21.5905 2.56638 21.9837 2.70011 22.4066C2.84758 22.873 3.21319 23.2382 3.67975 23.3851C4.10282 23.5184 4.49584 23.393 4.67791 23.3312C4.88863 23.2596 5.14253 23.1478 5.4047 23.0324L11.9957 20.1324L18.5868 23.0325C18.8489 23.1478 19.1028 23.2596 19.3135 23.3312C19.4956 23.393 19.8886 23.5184 20.3117 23.3851C20.7783 23.2382 21.1439 22.873 21.2913 22.4066C21.4251 21.9837 21.3002 21.5905 21.2385 21.4084C21.1672 21.1976 21.0558 20.9436 20.9407 20.6813L13.6295 4.01167C13.5266 3.77695 13.424 3.54301 13.3238 3.36083C13.2319 3.19367 13.039 2.86552 12.6657 2.67916Z" fill="white"/>
                </svg>
            </div>`;

        return new mapboxgl.Marker(el);
    }, []);

    const clearMarkers = useCallback(() => {
        markersRef.current.completed.forEach((m) => m.remove());
        markersRef.current.completed = [];

        if (markersRef.current.current) {
            markersRef.current.current.remove();
            markersRef.current.current = null;
        }
    }, []);

    const findClosestCoord = useCallback((targetCoord: [number, number], routeCoords: [number, number][]) => {
        let minDist = Infinity;
        let closest = routeCoords[0];
        routeCoords.forEach((coord) => {
            const dist = turf.distance(turf.point(targetCoord), turf.point(coord), { units: 'meters' });

            if (dist < minDist) {
                minDist = dist;
                closest = coord;
            }
        });

        return closest;
    }, []);

    const findClosestIndex = useCallback((targetCoord: [number, number], coordsArray: [number, number][]) => {
        let minDist = Infinity;
        let closestIdx = 0;
        coordsArray.forEach((coord, idx) => {
            const dist = turf.distance(turf.point(targetCoord), turf.point(coord), { units: 'meters' });

            if (dist < minDist) {
                minDist = dist;
                closestIdx = idx;
            }
        });

        return closestIdx;
    }, []);

    // Delegates to the shared segmented fetcher (25-coord chunking + max-distance/NoRoute
    // pairwise splits) so long routes don't 422 like the raw single-request version did.
    // Returns coords + the API's route distance so Remaining Distance can fall back to
    // the exact same number as Total Distance when nothing has moved yet.
    const getDrivingRoute = useCallback(
        async (start: { lat: number; lng: number }, waypoints: { lat: number; lng: number }[], end: { lat: number; lng: number }, preventCrossBorder = false): Promise<DrivingRouteResult> =>
            getDrivingRouteWithWaypoints(start, waypoints, end, preventCrossBorder, mapboxgl.accessToken),
        []
    );

    // ── Route + markers (identical logic to RadarOrderDetailNew) ────────────

    const updateRouteAndMarkers = useCallback(async () => {
        if (!mapRef.current || !mapReadyRef.current || !trackOrderDetail) return;
        const legs = trackOrderDetail?.legs ?? [];
        if (legs.length === 0) return;

        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];
        const shipperLat = Number(firstLeg?.shipper?.latitude);
        const shipperLng = Number(firstLeg?.shipper?.longitude);
        const consigneeLat = Number(lastLeg?.consignee?.latitude);
        const consigneeLng = Number(lastLeg?.consignee?.longitude);
        if (!shipperLat || !shipperLng || !consigneeLat || !consigneeLng) return;

        if (markersRef.current.current) {
            markersRef.current.current.remove();
            markersRef.current.current = null;
        }

        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }

        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
            animationTimeoutRef.current = null;
        }

        // Bump-and-capture so stale Directions API fetches can be discarded. MUST bump (not just
        // read): two overlapping calls that captured the same id would both pass the staleness
        // check below, each add an arrow marker, and the second would overwrite
        // markersRef.current.current — orphaning the first arrow on the map with no way to remove it.
        //
        // Bumped only AFTER the teardown above: bumping before the early returns would advance the
        // counter, killing the running animation loop, while leaving the old arrow on the map with
        // no pending redraw to remove it — a frozen marker over the previous order's route.
        const requestId = bumpShareRouteRequestSeq();

        const orderStatus = trackOrderDetail?.orderStatus;
        const isBookedOrDispatched = [ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED].includes(orderStatus);
        const hasLocationUpdates = !isBookedOrDispatched;
        // Newest-first list — getDrivingRoute's shared fetcher reverses internally,
        // so don't reverse here (it would double-reverse back to newest-first).
        // STARTED legs only — pings on booked/dispatched legs must not drive the truck
        // marker or the traveled split (see updateRouteAndMarkers in RadarOrderDetail).
        const allLocationUpdates = hasLocationUpdates ? getStartedLegLocationUpdates(legs) : [];

        const shipperCountry = firstLeg?.shipper?.country;
        const consigneeCountry = lastLeg?.consignee?.country;
        const isSameCountry = Boolean(shipperCountry && consigneeCountry && shipperCountry === consigneeCountry);
        const hasOutOfCountry = allLocationUpdates.some((u: any) => u?.country && u.country !== shipperCountry);
        const preventCrossBorder = isSameCountry && !hasOutOfCountry;

        // Truck position = the newest ping BY TIMESTAMP across STARTED legs — never the
        // last leg that happens to have a ping (a not-yet-started leg's stale ping would win).
        const lastLocation: any = getLatestStartedLegLocation(legs);

        const hasRealLocation = Boolean(lastLocation?.longitude) && !isBookedOrDispatched;
        const activeShipperLng = hasRealLocation ? Number(lastLocation?.longitude) : shipperLng;
        const activeShipperLat = hasRealLocation ? Number(lastLocation?.latitude) : shipperLat;

        const {
            coords: allCoordinates,
            distanceMiles: routeDistanceMiles,
            measureCoords,
        } = await getDrivingRoute(
            { lat: shipperLat, lng: shipperLng },
            allLocationUpdates.map((u: any) => ({ lat: Number(u.latitude), lng: Number(u.longitude) })),
            { lat: consigneeLat, lng: consigneeLng },
            preventCrossBorder
        );
        // Discard stale fetch: another updateRouteAndMarkers call was made while this was awaiting
        if (requestId !== getShareRouteRequestSeq()) return;
        if (!mapRef.current || !mapReadyRef.current) return;
        if (allCoordinates.length === 0) return;

        const isCompletedOrder = ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(orderStatus);
        let traveledCoords: [number, number][] = [];
        let remainingCoords: [number, number][] = allCoordinates;
        let splitIndex = 0;

        if (isCompletedOrder) {
            traveledCoords = allCoordinates;
            remainingCoords = [];
        } else if (hasRealLocation) {
            const snappedSplit = findClosestCoord([activeShipperLng, activeShipperLat], allCoordinates);
            splitIndex = findClosestIndex(snappedSplit, allCoordinates);
            traveledCoords = allCoordinates.slice(0, splitIndex + 1);
            remainingCoords = allCoordinates.slice(splitIndex);
        }

        // ── Remaining distance (mirrors WAL radar) ─────────────────────────────
        // Completed: 0. In-transit with location: truck → consignee measured along the
        // route. No pings yet: nothing has moved, so reuse Mapbox's full-route distance
        // instead of re-measuring with turf — a turf re-measure drifts a mile or two and
        // would make the Total and Remaining cards disagree at 0% progress.
        if (allCoordinates.length > 1) {
            if (isCompletedOrder) {
                dispatch(setRemainingDistance(0));
            } else if (hasRealLocation && remainingCoords.length > 1) {
                // Measure on the UNSIMPLIFIED geometry (measureCoords) so the figure matches WAL —
                // see the same branch in Track/RadarOrderDetail/index.tsx.
                const rawRemainingMiles = getRemainingRouteMiles(measureCoords, [activeShipperLng, activeShipperLat]);
                const remainingLine = turf.lineString(remainingCoords);
                dispatch(setRemainingDistance(rawRemainingMiles ?? parseFloat(turf.length(remainingLine, { units: 'miles' }).toFixed(2))));
            } else if (hasRealLocation) {
                // Split landed on the route's final point — truck at destination, remaining
                // is 0, not the full-route fallback (which would show Remaining = Total).
                dispatch(setRemainingDistance(0));
            } else {
                dispatch(setRemainingDistance(routeDistanceMiles));
            }
        }

        const traveledGeoJson = { type: 'Feature', geometry: { type: 'LineString', coordinates: traveledCoords.length > 1 ? traveledCoords : [] } } as any;

        if (mapRef.current?.getSource('route-traveled')) {
            (mapRef.current.getSource('route-traveled') as mapboxgl.GeoJSONSource).setData(traveledGeoJson);
        } else {
            mapRef.current?.addSource('route-traveled', { type: 'geojson', data: traveledGeoJson } as any);
            mapRef.current?.addLayer({ id: 'route-traveled-line', type: 'line', source: 'route-traveled', paint: { 'line-color': '#7A5AF8', 'line-width': 6, 'line-opacity': 0.5 } });
        }

        const remainingGeoJson = { type: 'Feature', geometry: { type: 'LineString', coordinates: remainingCoords } } as any;

        if (mapRef.current?.getSource('route-remaining')) {
            (mapRef.current.getSource('route-remaining') as mapboxgl.GeoJSONSource).setData(remainingGeoJson);
        } else {
            mapRef.current?.addSource('route-remaining', { type: 'geojson', data: remainingGeoJson } as any);
            mapRef.current?.addLayer({ id: 'route-remaining-line', type: 'line', source: 'route-remaining', paint: { 'line-color': '#444CE7', 'line-width': 6 } });
        }

        // IN_TRANSIT but no GPS location update yet — hide the static shipper (start) marker and
        // render the animated location arrow at the shipper point instead (see arrow block below).
        const isInTransitNoLocation = orderStatus === ORDER_STATUS.IN_TRANSIT && !hasRealLocation;

        const styleKey = isBookedOrDispatched ? 'default' : isCompletedOrder ? 'completed' : hasRealLocation ? 'inTransit' : isInTransitNoLocation ? 'inTransitNoLocation' : 'default';
        const needsRebuild = completedMarkerStyleRef.current !== styleKey || markersRef.current.completed.length === 0;

        if (needsRebuild) {
            markersRef.current.completed.forEach((m) => m.remove());
            markersRef.current.completed = [];

            // null start marker → in-transit with no GPS location: the animated location arrow
            // (drawn below) stands in for the shipper point, so we skip the static start marker.
            let startM: mapboxgl.Marker | null;
            let endM: mapboxgl.Marker;

            if (isBookedOrDispatched) {
                startM = createStartMarker();
                endM = createStartMarker();
            } else if (isCompletedOrder) {
                startM = createStartMarkerActive();
                endM = createEndMarkerActive();
            } else if (hasRealLocation) {
                startM = createStartMarkerActive();
                endM = createStartMarker();
            } else if (isInTransitNoLocation) {
                startM = null;
                endM = createStartMarker();
            } else {
                startM = createStartMarker();
                endM = createStartMarker();
            }

            if (startM) {
                startM.setLngLat([allCoordinates[0][0], allCoordinates[0][1]]).addTo(mapRef.current!);
                const shipperLabel = [firstLeg?.shipper?.city, firstLeg?.shipper?.stateCode].filter(Boolean).join(', ');

                if (shipperLabel) {
                    const popup = new mapboxgl.Popup({ offset: [0, -12], closeButton: false, className: 'map-marker-popup', closeOnClick: false, anchor: 'bottom' }).setHTML(
                        `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${shipperLabel}</div>`
                    );
                    startM.setPopup(popup).togglePopup();
                }
            }

            const snappedEnd = findClosestCoord([consigneeLng, consigneeLat], allCoordinates);
            endM.setLngLat(snappedEnd).addTo(mapRef.current!);
            const consigneeLabel = [lastLeg?.consignee?.city, lastLeg?.consignee?.stateCode].filter(Boolean).join(', ');

            if (consigneeLabel) {
                const popup = new mapboxgl.Popup({ offset: [0, -12], closeButton: false, className: 'map-marker-popup', closeOnClick: false, anchor: 'bottom' }).setHTML(
                    `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${consigneeLabel}</div>`
                );
                endM.setPopup(popup).togglePopup();
            }

            if (startM) {
                markersRef.current.completed.push(startM);
            }

            markersRef.current.completed.push(endM);

            // Junction dots carry the same always-visible city/state tooltip as the
            // shipper/consignee markers (matches RadarOrderDetail).
            if (legs.length > 1) {
                for (let i = 0; i < legs.length - 1; i++) {
                    const jLat = Number(legs[i]?.consignee?.latitude);
                    const jLng = Number(legs[i]?.consignee?.longitude);
                    if (!jLat || !jLng) continue;
                    const snapped = findClosestCoord([jLng, jLat], allCoordinates);
                    const waypointM = createWaypointMarker();
                    waypointM.setLngLat(snapped).addTo(mapRef.current!);
                    const junctionLabel = [legs[i]?.consignee?.city, legs[i]?.consignee?.stateCode].filter(Boolean).join(', ');

                    if (junctionLabel) {
                        const popup = new mapboxgl.Popup({
                            offset: [0, -12],
                            closeButton: false,
                            className: 'map-marker-popup',
                            closeOnClick: false,
                            anchor: 'bottom',
                        }).setHTML(
                            `<div style="font-size:12px;font-weight:500;color:#F9F9F9;background:#0C111B;padding:4px 8px;border-radius:8px;border:1px solid #0C111B;white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.15);line-height:1.4;">${junctionLabel}</div>`
                        );
                        waypointM.setPopup(popup).togglePopup();
                    }

                    markersRef.current.completed.push(waypointM);
                }
            }

            completedMarkerStyleRef.current = styleKey;
        }

        // ── Animated truck marker ─────────────────────────────────────────────
        // - Live GPS location exists → arrow snaps to that location.
        // - IN_TRANSIT with no GPS location yet → arrow stands in for the (hidden) shipper marker,
        //   starting at the shipper point and animating forward along the route.
        // activeShipperLng/Lat already falls back to the shipper point when there is no GPS ping.
        if (orderStatus === ORDER_STATUS.IN_TRANSIT) {
            const snappedCoords = findClosestCoord([activeShipperLng, activeShipperLat], allCoordinates);

            const animateMarkerAlongLine = (marker: mapboxgl.Marker, routeCoords: [number, number][], startIdx: number) => {
                const remainingRouteCoords = routeCoords.slice(startIdx);

                // Truck already at/next to the destination: fewer than 2 coords remain past
                // the split point — turf.lineString throws on <2 positions and there is
                // nothing left to animate along anyway.
                if (remainingRouteCoords.length < 2) return;

                const line = turf.lineString(remainingRouteCoords);
                const routeLength = turf.length(line, { units: 'meters' });
                const speedMetersPerSecond = 1;
                const distanceThresholdMeters = 50000;
                let distanceAlongRoute = 0;
                let lastTimestamp: number | null = null;
                const originalTraveledCoords = routeCoords.slice(0, startIdx + 1);

                const step = (timestamp?: number) => {
                    // Zombie-loop kill switch: a newer route draw supersedes this loop. Without this
                    // it keeps rewriting route-traveled / route-remaining and fights the new loop.
                    if (requestId !== getShareRouteRequestSeq()) return;

                    if (!timestamp) {
                        animationFrameRef.current = requestAnimationFrame(step);

                        return;
                    }

                    if (!lastTimestamp) {
                        lastTimestamp = timestamp;
                        animationFrameRef.current = requestAnimationFrame(step);

                        return;
                    }
                    const elapsed = (timestamp - lastTimestamp) / 1000;
                    lastTimestamp = timestamp;
                    distanceAlongRoute += speedMetersPerSecond * elapsed;
                    if (distanceAlongRoute >= distanceThresholdMeters) distanceAlongRoute = 0;
                    const routeLengthKm = routeLength / 1000;
                    const currentPoint = turf.along(line, distanceAlongRoute / 1000, { units: 'kilometers' });
                    const coordsData = currentPoint.geometry.coordinates as [number, number];
                    const nextDist = Math.min(distanceAlongRoute + 5, routeLength * 1000);
                    const nextPoint = turf.along(line, nextDist / 1000, { units: 'kilometers' });
                    const nextCoords = nextPoint.geometry.coordinates as [number, number];
                    const bearing = turf.bearing(turf.point(coordsData), turf.point(nextCoords));
                    marker.setLngLat(coordsData);
                    const svgArrow = marker.getElement().querySelector('#marker-arrow-svg') as HTMLElement;
                    if (svgArrow) svgArrow.style.transform = `rotate(${bearing}deg)`;
                    currentMarkerPositionRef.current = coordsData;

                    if (mapRef.current?.getSource('route-traveled') && mapRef.current?.getSource('route-remaining')) {
                        const distKm = Math.max(0, Math.min(distanceAlongRoute / 1000, routeLengthKm));

                        if (distKm > 0 && routeLengthKm > 0) {
                            const animTraveled = turf.lineSliceAlong(line, 0, distKm, { units: 'kilometers' }).geometry.coordinates;
                            const animRemaining = distKm < routeLengthKm ? turf.lineSliceAlong(line, distKm, routeLengthKm, { units: 'kilometers' }).geometry.coordinates : [];
                            const fullTraveled = [...originalTraveledCoords, ...animTraveled.slice(1)];
                            (mapRef.current.getSource('route-traveled') as mapboxgl.GeoJSONSource).setData({
                                type: 'Feature',
                                geometry: { type: 'LineString', coordinates: fullTraveled.length > 1 ? fullTraveled : [] },
                            } as any);
                            (mapRef.current.getSource('route-remaining') as mapboxgl.GeoJSONSource).setData({
                                type: 'Feature',
                                geometry: { type: 'LineString', coordinates: animRemaining.length > 1 ? animRemaining : [] },
                            } as any);
                        }
                    }
                    animationFrameRef.current = requestAnimationFrame(step);
                };

                animationFrameRef.current = requestAnimationFrame(step);
            };

            const currentMarker = createCurrentMarker();
            currentMarker.setLngLat(snappedCoords).addTo(mapRef.current!);
            markersRef.current.current = currentMarker;
            animationTimeoutRef.current = window.setTimeout(() => {
                animationTimeoutRef.current = null;

                // Bail if a newer draw started while this delayed start was pending.
                if (requestId !== getShareRouteRequestSeq()) return;

                animateMarkerAlongLine(currentMarker, allCoordinates, splitIndex);
            }, 1000) as unknown as number;
        }

        const bounds = new mapboxgl.LngLatBounds();
        allCoordinates.forEach((coord) => bounds.extend(coord));
        if (lastLocation?.latitude && lastLocation?.longitude) bounds.extend([Number(lastLocation.longitude), Number(lastLocation.latitude)]);
        routeBoundsRef.current = bounds;
        fitMapToRoute();
    }, [
        trackOrderDetail,
        getDrivingRoute,
        findClosestCoord,
        findClosestIndex,
        createStartMarker,
        createStartMarkerActive,
        createEndMarkerActive,
        createWaypointMarker,
        createCurrentMarker,
        fitMapToRoute,
    ]);

    // ── Cleanup on unmount / id change ──────────────────────────────────────

    useEffect(() => {
        currentMarkerPositionRef.current = null;
        completedMarkerStyleRef.current = '';

        return () => {
            clearMarkers();

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }

            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
            mapReadyRef.current = false;
            routeBoundsRef.current = null;
        };
    }, [clearMarkers]);

    const updateRouteRef = useRef(updateRouteAndMarkers);
    useEffect(() => {
        updateRouteRef.current = updateRouteAndMarkers;
    });

    // ── Map initialization ───────────────────────────────────────────────────

    useEffect(() => {
        if (mapRef.current || !mapContainerRef.current) return;
        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mapbox/light-v11',
            center: [-93.27, 42.39],
            zoom: 3,
            pitch: 0,
            bearing: 0,
            boxZoom: true,
            antialias: true,
            projection: 'mercator',
            dragRotate: false,
        });
        mapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
        mapRef.current.on('load', () => {
            mapReadyRef.current = true;
            setMapReady(true); // triggers the route-drawing effect if data is already loaded
            mapRef.current?.resize();
            updateRouteRef.current();
        });

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
                animationTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (mapRef.current && mapReadyRef.current) updateRouteAndMarkers();
    }, [trackOrderDetail?.orderStatus, trackOrderDetail?.legs, trackOrderDetail?.id, mapReady]);

    const handleRecenterMap = useCallback(() => {
        fitMapToRoute();
    }, [fitMapToRoute]);

    // Jump the camera in close on the live truck arrow. Prefers the animated position ref (kept in
    // step with the marker as it interpolates along the route); falls back to the marker's own
    // LngLat when the ref hasn't been populated yet — e.g. the very first frame after a redraw.
    // Mirrors RadarOrderDetailNew.handleZoomToTruck, reusing getMapFitPadding so the arrow lands in
    // the visible part of the map rather than behind the order panel / bottom sheet.
    const handleZoomToTruck = useCallback(() => {
        if (!mapRef.current) return;

        const arrowPos = currentMarkerPositionRef.current || (markersRef.current.current ? markersRef.current.current.getLngLat().toArray() : null);

        if (!arrowPos) return;

        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend(arrowPos as [number, number]);

        mapRef.current.fitBounds(bounds, {
            padding: getMapFitPadding(),
            maxZoom: 17,
            // Instant jump — no smooth camera glide.
            animate: false,
        });
    }, []);

    // ── Mobile bottom sheet ──────────────────────────────────────────────────

    const handleSheetTouchStart = (e: React.TouchEvent) => {
        touchStartYRef.current = e.touches[0].clientY;
    };

    const handleSheetTouchMove = (e: React.TouchEvent) => {
        const delta = e.touches[0].clientY - touchStartYRef.current;
        setSheetDragOffset(delta);
    };

    const handleSheetTouchEnd = () => {
        if (sheetSnap === 'expanded' && sheetDragOffset > 80) {
            setSheetSnap('default');
        } else if (sheetSnap === 'default') {
            if (sheetDragOffset > 80) setSheetSnap('collapsed');
            else if (sheetDragOffset < -80) setSheetSnap('expanded');
        } else if (sheetSnap === 'collapsed' && sheetDragOffset < -60) {
            setSheetSnap('default');
        }

        setSheetDragOffset(0);
    };

    useEffect(() => {
        const el = sheetScrollRef.current;

        if (!el) return;

        const stopProp = (e: TouchEvent) => e.stopPropagation();

        el.addEventListener('touchstart', stopProp, { passive: true, capture: true });
        el.addEventListener('touchmove', stopProp, { passive: true, capture: true });

        return () => {
            el.removeEventListener('touchstart', stopProp, { capture: true });
            el.removeEventListener('touchmove', stopProp, { capture: true });
        };
    }, []);

    const getSheetTop = () => {
        const vh = window.innerHeight;

        if (sheetSnap === 'expanded') return `${Math.max(60, 60 + sheetDragOffset)}px`;
        if (sheetSnap === 'default') return `${Math.round(vh * 0.6) + sheetDragOffset}px`;

        return `${Math.max(vh - 72, vh - 72 + sheetDragOffset)}px`;
    };

    useEffect(() => {
        // Keep the ref in sync so getMapFitPadding reads the right snap value
        // by the time the 350 ms timer fires.
        sheetSnapRef.current = sheetSnap;

        const timer = setTimeout(() => {
            mapRef.current?.resize();
            fitMapToRoute(); // recenter the route with updated bottom padding
        }, 350);

        return () => clearTimeout(timer);
    }, [sheetSnap, fitMapToRoute]);

    // ── First paint ──────────────────────────────────────────────────────────

    // Ready = the detail answered AND the map drew once. An error state replaces the frame
    // entirely, so it counts as ready too — the cover must never sit on top of an error.
    const isContentReady = state === LOAD_STATE.SUCCESS ? mapReady : state !== LOAD_STATE.LOADING;

    // up -> fading: a settle beat after ready, so the route Mapbox draws on its 'load' handler is on
    // screen before the cover starts clearing, rather than appearing through it.
    useEffect(() => {
        if (!isContentReady || coverPhase !== 'up') return undefined;

        const timer = window.setTimeout(() => setCoverPhase('fading'), FIRST_PAINT_SETTLE_MS);

        return () => window.clearTimeout(timer);
    }, [isContentReady, coverPhase]);

    // fading -> gone: dropped from the tree only after the fade has run, so it cannot cut its own
    // transition short and cannot swallow a click on the map underneath once it is invisible.
    useEffect(() => {
        if (coverPhase !== 'fading') return undefined;

        const timer = window.setTimeout(() => setCoverPhase('gone'), FIRST_PAINT_FADE_MS);

        return () => window.clearTimeout(timer);
    }, [coverPhase]);

    // A map that never fires 'load' (missing token, offline, WebGL unavailable) must not leave the
    // cover up for good. After this the page is shown however far along it is — the same half-built
    // view as before, which is the right fallback: visible beats hidden.
    useEffect(() => {
        const timer = window.setTimeout(() => setCoverPhase('gone'), FIRST_PAINT_MAX_MS);

        return () => window.clearTimeout(timer);
    }, []);

    // ── Error states ─────────────────────────────────────────────────────────

    const renderErrorOrLoading = () => {
        if (state === LOAD_STATE.INVALID_TOKEN) return <ErrorState title={t('ShareRadarOrder.Invalid_link_title') as string} body={t('ShareRadarOrder.Invalid_link_body') as string} />;
        if (state === LOAD_STATE.EXPIRED) return <ErrorState title={t('ShareRadarOrder.Expired_title') as string} body={t('ShareRadarOrder.Expired_body') as string} />;
        if (state === LOAD_STATE.NOT_FOUND) return <ErrorState title={t('ShareRadarOrder.Not_found_title') as string} body={t('ShareRadarOrder.Not_found_body') as string} />;
        if (state === LOAD_STATE.CANCELLED) return <ErrorState title={t('ShareRadarOrder.Cancelled_title') as string} body={t('ShareRadarOrder.Cancelled_body') as string} />;
        if (state === LOAD_STATE.ERROR) return <ErrorState title={t('ShareRadarOrder.Error_title') as string} body={errorMessage || (t('ShareRadarOrder.Error_body') as string)} />;

        return null;
    };

    const errorContent = renderErrorOrLoading();

    return (
        <>
            <main className="h-screen flex flex-col bg-utilityBlack700">
                {/* Share pages render OUTSIDE AppLayout, which is where the app's only other
                    ToastBodyContainer lives — without this the copy-to-clipboard toast never
                    appears. Same reason SharedQuote mounts its own. */}
                <ToastBodyContainer />
                <SharedPageTopBar centerContent={publicTracking?.searchContent} />
                <div className="flex-1 flex flex-col min-h-0 lg:overflow-hidden w-full mx-auto lg:px-4 rounded-t-xl global-background-color">
                    <div className="flex flex-col flex-1 min-h-0 max-w-[1480px] w-full mx-auto lg:rounded-[20px] lg:overflow-hidden lg:p-5">
                        {errorContent ? (
                            <div className="flex-1 flex flex-col bg-utilityGray100 overflow-auto">{errorContent}</div>
                        ) : (
                            <div className="flex-1 relative overflow-hidden lg:rounded-[20px] shadow-[0px_0px_12px_0px_rgba(16,24,40,0.08),0px_4px_12px_-2px_rgba(16,24,40,0.10)]">
                                {/* First-paint cover — see coverPhase. Highest z in this frame (map controls
                                    sit at 12, the status veil at 2). */}
                                {coverPhase !== 'gone' && (
                                    <LoadingCover
                                        label={publicTracking?.loadingLabel || (t('ShareRadarOrder.Loading_shipment') as string)}
                                        phase={coverPhase}
                                        fadeMs={FIRST_PAINT_FADE_MS}
                                        className="z-[25] lg:rounded-[20px]"
                                    />
                                )}

                                {/* ── Map (full-screen on both desktop and mobile) ── */}
                                <div className="relative h-full w-full radar-mapbox z-[1] lg:rounded-[20px] ">
                                    <style>{`
                            .current-location-marker { width: 24px; height: 24px; cursor: pointer; }
                            .radar-mapbox .mapboxgl-ctrl-top-right { padding-top: 68px; padding-right: 8px; }
                            .map-marker-popup .mapboxgl-popup-content { padding: 0; background: transparent; box-shadow: none; }
                            .map-marker-popup .mapboxgl-popup-tip { display: none; }
                        `}</style>

                                    <div id="map" ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

                                    {trackOrderDetail && ![ORDER_STATUS.BOOKED, ORDER_STATUS.DISPATCHED, ORDER_STATUS.IN_TRANSIT].includes(trackOrderDetail?.orderStatus) && (
                                        <div className="absolute inset-0 bg-white/60 z-[2] pointer-events-none" />
                                    )}

                                    {mapRef.current && (
                                        /* Recenter + Zoom-to-truck — joined into one segmented box (matches the
                                            native Mapbox +/- control group): single white rounded card, buttons
                                            stacked with a divider, only outer corners rounded. */
                                        <div className="absolute z-[12] right-[18px] top-[145px] max-lg:top-[10px] flex flex-col rounded-md overflow-hidden border border-borderPrimary bg-white shadow-sm divide-y divide-borderPrimary">
                                            <TooltipCmp message={t('Radar.Recenter_Map')}>
                                                <ButtonCmp
                                                    className="!rounded-none !border-0 !shadow-none bg-white hover:bg-gray50"
                                                    size="square-sm"
                                                    onClick={handleRecenterMap}
                                                    icon={<Move className="w-4 h-4 text-textSecondary" />}
                                                >
                                                    <></>
                                                </ButtonCmp>
                                            </TooltipCmp>

                                            {/* Zoom-to-truck — in-transit only: there is no live arrow marker to
                                                zoom to in any other status, so the button would be a no-op. */}
                                            {trackOrderDetail?.orderStatus === ORDER_STATUS.IN_TRANSIT && (
                                                <TooltipCmp message={t('Radar.Zoom_To_Truck')}>
                                                    <ButtonCmp
                                                        className="!rounded-none !border-0 !shadow-none bg-white hover:bg-gray50"
                                                        size="square-sm"
                                                        onClick={handleZoomToTruck}
                                                        icon={<Target03 className="w-4 h-4 text-textSecondary" />}
                                                    >
                                                        <></>
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                            )}
                                        </div>
                                    )}

                                    {/* Mobile: order ID badge + a separate "Updated" badge beside it, top-left of the map */}
                                    <div className="lg:hidden absolute top-1.5 left-1.5 right-14 z-[12] pointer-events-none flex items-center gap-1.5">
                                        <span
                                            className={`flex-none flex items-center px-1.5 py-0.5 rounded-md border border-borderPrimary bg-white shadow-xs text-[10px] leading-[14px] font-medium text-textSecondary whitespace-nowrap ${
                                                isDetailLoading ? 'custom-loading' : ''
                                            }`}
                                        >
                                            {isDetailLoading ? '#WAL-XXXX-XXXX' : `#${trackOrderDetail?.orderId ?? ''}`}
                                        </span>
                                        {!isDetailLoading && headerLastUpdate && (
                                            <span className="min-w-0 truncate px-1.5 py-0.5 rounded-md border border-borderPrimary bg-white shadow-xs text-[10px] leading-[14px] font-medium text-textSecondary whitespace-nowrap">
                                                {`${t('ShareRadarOrder.Updated')} ${headerLastUpdate}`}
                                            </span>
                                        )}
                                    </div>

                                    <div className="max-lg:hidden">
                                        <RadarMapAboveComponentNew isShowDeliveredCard={isShowDeliveredCard} setIsShowDeliveredCard={setIsShowDeliveredCard} />
                                    </div>
                                </div>

                                {/* ── Desktop overlay: floating header + left sidebar (lg+) ── */}
                                <div className="max-lg:hidden absolute px-4 pt-4 w-full space-y-2 h-full flex flex-col overflow-hidden [overflow-x:clip] top-0 left-0">
                                    <div className="radar-header-blur z-[10] rounded-t-[20px]">
                                        <Header
                                            title={
                                                <div className="flex items-center md:gap-2 gap-1">
                                                    <div className="flex flex-col">
                                                        <p className={`${isDetailLoading ? 'custom-loading' : ''}`}>{isDetailLoading ? 'WAL-XXXX-XXXX' : `#${trackOrderDetail?.orderId ?? ''}`}</p>
                                                    </div>
                                                    {plainStatusBadge}
                                                    {headerMeta}
                                                </div>
                                            }
                                            leftClassName="!w-auto"
                                            rightClassName="!w-auto"
                                            mainContainerClassName="!flex-nowrap !flex-row z-[2] w-full !items-center"
                                            rightSideContent={
                                                <div className="flex items-center sm:gap-x-2.5 gap-x-2">
                                                    <TooltipCmp message={isVisible ? 'Hide Panel' : 'Show Panel'} childrenClassName="flex">
                                                        <ButtonCmp
                                                            size="square-sm"
                                                            className="btn_secondary_black"
                                                            icon={isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                            onClick={() => setIsVisible((prev) => !prev)}
                                                        >
                                                            <></>
                                                        </ButtonCmp>
                                                    </TooltipCmp>
                                                </div>
                                            }
                                        />
                                    </div>
                                    <div
                                        className={`lg:w-[537px] flex-1 overflow-auto scrollbar-hide z-[2] custom-scrollbar-v2 flex flex-col lg:pb-4 pt-2 space-y-2 transition-transform duration-300 ease-[cubic-bezier(.25,.1,.25,1)] ${
                                            isVisible ? 'translate-x-0 px-2 -mx-2' : '-translate-x-full px-2 -mx-3'
                                        }`}
                                    >
                                        <OrderMetaCards />

                                        <div className="flex flex-col space-y-2">
                                            {isDetailLoading || resolvedScheduleSection === 'pickup' ? (
                                                <PickupStateSection isShareLink={true} titleContent={scheduleSwitcher} />
                                            ) : resolvedScheduleSection === 'inTransit' ? (
                                                <InTransitStateSection isShareLink={true} titleContent={scheduleSwitcher} />
                                            ) : resolvedScheduleSection === 'delivery' && trackOrderDetail?.orderStatus ? (
                                                <DeliveredStateSection isShareLink={true} titleContent={scheduleSwitcher} />
                                            ) : null}

                                            {/* First of the detail cards, above Location Updates (client, 2026-10-08:
                                                documents are what a recipient came for, so they must not sit at the bottom
                                                of the column). Renders nothing when the customer shares no documents, so a
                                                link with every toggle off keeps exactly the order it had. */}
                                            <SharedDocumentsSection documents={(trackOrderDetail as any)?.documents} kind="radar" />

                                            {/* Under the schedule card and the documents, above Carrier Details (Lucas,
                                                2026-10-01: "for now"). Same gate as CP Track + wal-frontend-v2 Radar: hidden
                                                only pre-pickup (booked/dispatched — no updates exist yet), shown from
                                                in-transit onward so history stays visible after delivery. */}
                                            {trackOrderDetail?.orderStatus !== ORDER_STATUS.BOOKED && trackOrderDetail?.orderStatus !== ORDER_STATUS.DISPATCHED && <LocationUpdatesSection />}
                                            <CarrierDetailsSection />
                                            <OrderDetailsSection hideInternalReferenceRows />
                                            {/* No Latest Activity feed here on purpose: the activity log names WAL users and
                                                lists internal edits (contact/schedule/value changes), which the client treats as
                                                internal notes. Milestones a recipient needs are already on this page — the status
                                                badge, actual pickup/delivery timestamps on the stop cards, and the location
                                                history above. */}
                                        </div>
                                    </div>
                                </div>

                                {/* ── Mobile bottom sheet — fixed overlay over map (max-lg) ── */}
                                <div
                                    className="lg:hidden fixed inset-x-0 bottom-0 z-[20] bg-white rounded-t-2xl shadow-[0px_-1px_13px_5px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden"
                                    style={{
                                        top: getSheetTop(),
                                        transition: sheetDragOffset !== 0 ? 'none' : 'top 300ms cubic-bezier(0.25, 0.1, 0.25, 1)',
                                    }}
                                >
                                    {/* Drag handle + peek row — full draggable header area */}
                                    <div
                                        className="flex-shrink-0 touch-none select-none cursor-grab active:cursor-grabbing"
                                        onTouchStart={handleSheetTouchStart}
                                        onTouchMove={handleSheetTouchMove}
                                        onTouchEnd={handleSheetTouchEnd}
                                        onClick={() =>
                                            sheetDragOffset === 0 &&
                                            (sheetSnap === 'collapsed' ? setSheetSnap('default') : sheetSnap === 'default' ? setSheetSnap('expanded') : setSheetSnap('default'))
                                        }
                                    >
                                        <div className="flex flex-col gap-3 px-3 pt-1 pb-3">
                                            <div className="flex justify-center items-center h-4">
                                                <div className="w-[69px] h-1 bg-utilityGray200 rounded-full" />
                                            </div>
                                            {/* Delivered: the existing Delivery Completed card (with its Close) stays as-is;
                                                once closed — or for any other status — the compact status card shows. */}
                                            {!isDetailLoading && isDeliveredStatus && isShowDeliveredCard ? (
                                                <div onClick={(e) => e.stopPropagation()}>
                                                    <RadarMapAboveComponentNew isInline isShowDeliveredCard={isShowDeliveredCard} setIsShowDeliveredCard={setIsShowDeliveredCard} />
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-1.5 p-2 rounded-xl border border-utilityGray200 bg-white shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]">
                                                    <div
                                                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-none ${isDeliveredStatus ? 'bg-utilitySuccess100' : 'bg-primary100'} ${
                                                            isDetailLoading ? 'custom-loading' : ''
                                                        }`}
                                                    >
                                                        {isDeliveredStatus ? <CheckCircle className="w-4 h-4 text-fgSuccessPrimary" /> : <Truck01 className="w-4 h-4 text-primary" />}
                                                    </div>
                                                    <div className="flex-1 min-w-0 flex flex-col">
                                                        {/* Loading: bars are 2px shorter than their 18px / 14px line boxes and centred in them,
                                                            so they don't touch — card height is unchanged, no shift when data arrives. */}
                                                        {isDetailLoading ? (
                                                            <p className="h-[18px] flex items-center">
                                                                <span className="custom-loading inline-block text-xs leading-4 font-semibold">lorem ipsum</span>
                                                            </p>
                                                        ) : (
                                                            <p className="text-xs leading-[18px] font-semibold text-grayLight900 truncate">{statusCardTitle}</p>
                                                        )}
                                                        {isDetailLoading ? (
                                                            <p className="h-[14px] flex items-center">
                                                                <span className="custom-loading inline-block text-[10px] leading-3 font-normal">lorem ipsum dolor sit amet</span>
                                                            </p>
                                                        ) : (
                                                            statusCardSubtitleHtml && (
                                                                <p
                                                                    className="text-[10px] leading-[14px] font-normal text-textSecondary truncate"
                                                                    dangerouslySetInnerHTML={{ __html: statusCardSubtitleHtml }}
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Scrollable full content */}
                                    <div
                                        ref={sheetScrollRef}
                                        className="flex-1 min-h-0 overflow-y-scroll touch-pan-y custom-scrollbar-v2 px-3 pb-10 h-full"
                                        style={{ WebkitOverflowScrolling: 'touch' }}
                                    >
                                        {/* Tabs — sticky at the top of the existing scroll area */}
                                        <div className="sticky top-0 z-[2] bg-white pt-3">
                                            <div className="flex gap-4 border-b border-utilityGray200">
                                                {mobileTabs.map((tab) => (
                                                    <button
                                                        key={tab.value}
                                                        type="button"
                                                        onClick={() => {
                                                            setActiveMobileTab(tab.value);
                                                            // Start each tab at the top — otherwise a long tab's scroll offset is clamped
                                                            // onto a shorter one and the content jumps.
                                                            sheetScrollRef.current?.scrollTo({ top: 0 });
                                                        }}
                                                        className={`flex-1 min-w-0 h-8 px-1 pb-3 -mb-px flex items-center justify-center text-xs leading-[18px] font-semibold whitespace-nowrap border-b-2 ${
                                                            resolvedMobileTab === tab.value ? 'text-primary700 border-primary' : 'text-gray500 border-transparent'
                                                        }`}
                                                    >
                                                        {tab.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-3 pt-3">
                                            {resolvedMobileTab === 'overview' && <OrderMetaCards isShareMobile consigneeSubText={consigneeSubText} />}

                                            {resolvedMobileTab === 'schedule' && <LegSwitcher isShareMobile />}

                                            {resolvedMobileTab === 'schedule' &&
                                                (isDetailLoading || resolvedScheduleSection === 'pickup' ? (
                                                    <PickupStateSection isShareLink={true} isShareMobile titleContent={mobileScheduleSwitcher} />
                                                ) : resolvedScheduleSection === 'inTransit' ? (
                                                    <InTransitStateSection isShareLink={true} isShareMobile titleContent={mobileScheduleSwitcher} />
                                                ) : resolvedScheduleSection === 'delivery' && trackOrderDetail?.orderStatus ? (
                                                    <DeliveredStateSection isShareLink={true} isShareMobile titleContent={mobileScheduleSwitcher} />
                                                ) : null)}

                                            {resolvedMobileTab === 'order' && (
                                                <>
                                                    <OrderDetailsSection hideInternalReferenceRows isShareMobile />
                                                    <CarrierDetailsSection isShareMobile />
                                                    <SharedDocumentsSection documents={(trackOrderDetail as any)?.documents} kind="radar" isShareMobile />
                                                </>
                                            )}

                                            {/* Same gate as CP Track + wal-frontend-v2 Radar: the Updates tab only exists from
                                                in-transit onward. No Latest Activity feed on purpose (internal notes). */}
                                            {resolvedMobileTab === 'updates' && <LocationUpdatesSection isShareMobile />}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </>
    );
};

export default ShareRadarOrder;
